import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    DestroyRef,
    HostBinding,
    Input,
    OnDestroy,
    OnInit,
    ViewChild,
    inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivationEnd } from '@angular/router';

import { CountsFixturesResponse } from '@cds/betting-offer/domain-specific';
import { OfferMapping } from '@cds/query-objects';
import { EventModel } from '@frontend/sports/betting-offer/feature/model';
import { FavouritesConfig } from '@frontend/sports/common/client-config-data-access';
import { RouterEventsService } from '@frontend/sports/common/core/utils/router-events';
import { TimerService } from '@frontend/sports/common/core/utils/timer';
import { FavouritesService } from '@frontend/sports/favourites/core/feature';
import { UserService } from '@frontend/sports/user/feature';
import { first, map, merge, takeUntil } from 'rxjs';

import { BaseTreeItem, TreeItem, TreeItemType } from '../../event-details-common/event-details-common.models';
import { LiveFavouriteFixturesService } from '../../favourites/live-favourites-fixtures.service';
import { TreeScrollDirective } from '../../tree/tree-scroll.directive';
import { LiveEventsNavigationRefreshService } from './live-events-navigation-refresh.service';
import { LiveEventsNavigationRouteService } from './live-events-navigation-route.service';
import { LiveEventsNavigationSubscriptionService } from './live-events-navigation-subscription.service';
import { LiveEventsNavigationTreeService } from './live-events-navigation-tree.service';
import { LiveEventsNavigationService } from './live-events-navigation.service';

@Component({
    selector: 'ms-live-events-navigation',
    templateUrl: 'live-events-navigation.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class LiveEventsNavigationComponent implements OnInit, OnDestroy {
    @Input() event: EventModel;
    @HostBinding('class') className = 'event-details-left-menu';
    @ViewChild(TreeScrollDirective, { static: true }) treeScroll: TreeScrollDirective;
    tree?: BaseTreeItem;

    private stopped = false;
    private readonly destroyRef = inject(DestroyRef);

    constructor(
        public navigationService: LiveEventsNavigationService,
        private routerHelper: RouterEventsService,
        private subscriptionsService: LiveEventsNavigationSubscriptionService,
        private refreshService: LiveEventsNavigationRefreshService,
        private routeService: LiveEventsNavigationRouteService,
        private timerService: TimerService,
        private liveFavouriteFixturesService: LiveFavouriteFixturesService,
        private userService: UserService,
        private favouritesService: FavouritesService,
        private favouritesConfig: FavouritesConfig,
        private treeService: LiveEventsNavigationTreeService,
        private changeRef: ChangeDetectorRef,
    ) {}

    ngOnInit(): void {
        this.navigationService.tree$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((tree) => {
            this.tree = tree;
            this.changeRef.markForCheck();
        });
        this.loadEvents();

        // switching to landscape doesnt trigger route activation start event, so listen also to component destroyed
        const stopProcessing$ = this.routeService.stopProcessing$.pipe(first(), takeUntilDestroyed(this.destroyRef));

        // we need this when navigating from betslip, betfinder etc.
        this.routerHelper.currentActivationEnd
            .pipe(takeUntilDestroyed(this.destroyRef), takeUntil(stopProcessing$))
            .subscribe((data) => this.activationEnd(data));

        this.subscriptionsService.updatedEvents$.pipe(takeUntilDestroyed(this.destroyRef), takeUntil(stopProcessing$)).subscribe((events) => {
            this.navigationService.updateEvents(events);
            this.changeRef.markForCheck();
        });

        if (this.favouritesConfig.isLiveFavouritesEnabled) {
            this.liveFavouriteFixturesService.change.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
                this.treeService.updateFavourites(this.tree);
                this.navigationService.refreshFavouritesTreeItem();
                this.changeRef.markForCheck();
            });

            this.favouritesService.change.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
                if (this.userService.isAuthenticated) {
                    this.liveFavouriteFixturesService.refresh(OfferMapping.MainMarkets);
                }
                this.changeRef.markForCheck();
            });
        }

        merge(this.refreshService.refresh$.pipe(map(this.refreshTree)), this.refreshService.reload$.pipe(map(this.reloadTree)))
            .pipe(takeUntilDestroyed(this.destroyRef), takeUntil(stopProcessing$))
            .subscribe((updateTree) => {
                this.treeScroll.preserveScrolling(updateTree);
                this.changeRef.markForCheck();
            });

        stopProcessing$.subscribe(() => {
            this.unsubscribe();
        });
    }

    ngOnDestroy(): void {
        this.navigationService.reset();
        this.unsubscribe();
    }

    onSelectEvent(event: EventModel): void {
        this.navigationService.selectEvent(event);
    }

    onToggleMarkets(sport: TreeItem): void {
        this.navigationService.toggleMarkets(sport);
    }

    toggleItem(item: TreeItem): void {
        switch (item.type) {
            case TreeItemType.Sport:
                this.toggleSport(item);
                break;
            case TreeItemType.League:
                this.toggleLeague(item);
                break;
            case TreeItemType.Favourite:
            case TreeItemType.FavouriteLeague:
                this.toggleFavourites(item);
                break;

            default:
                throw new Error(`Unknown TreeItemType: ${item.type}`);
        }
    }

    loadEvents(): void {
        this.navigationService
            .loadVisibleLeagues()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => {
                this.scrollToEvent();
                this.changeRef.markForCheck();
            });
    }

    private refreshTree = (data: CountsFixturesResponse) => async () => this.navigationService.refreshTree(data);

    private reloadTree = (data: CountsFixturesResponse) => async () => this.navigationService.reloadTree(data);

    private toggleFavourites(item: TreeItem): void {
        if (item.type === TreeItemType.Favourite) {
            this.navigationService.toggleFavourites(item);
        } else {
            this.navigationService.toggleFavouriteLeague(item);
        }
    }

    private toggleSport(item: TreeItem): void {
        this.navigationService.toggleSport(item);
    }

    private toggleLeague(item: TreeItem): void {
        this.navigationService.toggleLeague(item);
    }

    private activationEnd(data?: ActivationEnd): void {
        if (data) {
            const event = <EventModel>data.snapshot.data['model'].model;
            if (event && event.live && !this.navigationService.isSelected(event.id)) {
                this.navigationService.selectEvent(event);
                this.expandEvent(event);
            }
        }
    }

    private scrollToEvent(): void {
        if (this.stopped) {
            return;
        }

        this.timerService.setTimeout(() => {
            this.treeScroll.scrollTo(this.navigationService.selectedEvent);
        }, 0);
    }

    private expandEvent(event: EventModel): void {
        this.navigationService.expandEvent(event).then(() => this.scrollToEvent());
    }

    private unsubscribe(): void {
        this.stopped = true;
        this.timerService.setTimeout(() => this.subscriptionsService.unsubscribeAll(), 0);
    }
}
